import React, { useState } from 'react';
import { getMatchPlayerStats } from '../services/serieaApi';
import EventPlugin from './EventPlugin';

/* ─── Helpers ────────────────────────────────────────────────────── */
function teamFull(team) {
  if (!team) return '?';
  if (typeof team === 'string') return team;
  return team.officialName || team.mediaName || team.shortName || team.acronymName || '?';
}
function fmtDate(raw) {
  if (!raw) return null;
  try {
    return new Date(raw).toLocaleDateString('it-IT', {
      weekday: 'long', day: '2-digit', month: 'long',
      year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return String(raw); }
}
function statusBadge(status) {
  if (!status) return null;
  const s = status.toUpperCase();
  if (s === 'LIVE' || s === 'INPROGRESS') return { text: '🔴 LIVE',         cls: 'status-live'     };
  if (s === 'FINISHED' || s === 'PLAYED') return { text: '✓ Terminata',     cls: 'status-finished' };
  if (s === 'UPCOMING' || s === 'FIXTURE')return { text: '⏱ In programma', cls: 'status-upcoming' };
  return { text: status, cls: 'status-other' };
}
function safeName(str) {
  return (str || '').replace(/\s+/g, '_').replace(/[^\w-]/g, '');
}

const MVP_EMAIL  = 'mvpmathandsport@gmail.com';
const S = { IDLE: 'idle', LOADING: 'loading', SUCCESS: 'success', ERROR: 'error' };

export default function AppLauncher({ match }) {
  const [showRaw,  setShowRaw]  = useState(false);
  const [mvpState, setMvpState] = useState(S.IDLE);
  const [mvpMsg,   setMvpMsg]   = useState('');

  /* ── Dati partita ─────────────────────────────────────────────── */
  const homeTeam  = match?.home || match?.homeTeam;
  const awayTeam  = match?.away || match?.awayTeam;
  const homeFull  = teamFull(homeTeam);
  const awayFull  = teamFull(awayTeam);
  const homeScore = match?.homeScorePush ?? match?.providerHomeScore ?? match?.homeScore ?? null;
  const awayScore = match?.awayScorePush ?? match?.providerAwayScore ?? match?.awayScore ?? null;
  const hasScore  = homeScore !== null && awayScore !== null;
  const rawDate   = match?.matchDateLocal || match?.kickOffDate || match?.date || null;
  const matchId   = match?.matchId || match?.optaId || match?.id || null;
  const seasonId  = match?.seasonId || null;
  const matchday  = match?.matchday || match?.matchDayNumber || match?.matchDayLabel;
  const stadium   = match?.stadiumName || match?.stadium?.name || null;
  const st        = statusBadge(match?.status);
  const baseName  = `${safeName(homeFull)}_vs_${safeName(awayFull)}`;

  const isPlayable = ['FINISHED','PLAYED','LIVE','INPROGRESS'].includes(
    (match?.status || '').toUpperCase()
  );

  /* ── MVP send ─────────────────────────────────────────────────── */
  async function handleSendMVP() {
    if (!matchId || !seasonId) {
      setMvpState(S.ERROR);
      setMvpMsg('Match ID o Season ID mancante.');
      return;
    }
    setMvpState(S.LOADING);
    setMvpMsg('Recupero statistiche giocatori…');
    try {
      const playerStatsJson = await getMatchPlayerStats(seasonId, matchId);

      setMvpMsg('Invio al backend MVP…');
      const jsonBlob = new Blob([JSON.stringify(playerStatsJson)], { type: 'application/json' });
      const formData = new FormData();
      formData.append('file',            jsonBlob, 'data.json');
      formData.append('filename_prefix', baseName);
      formData.append('email',           MVP_EMAIL);

      const response = await fetch('/api/send-mvp', { method: 'POST', body: formData });
      const data     = await response.json().catch(() => ({}));

      if (!response.ok || data.error) {
        throw new Error(data.error || data.detail || `Errore backend ${response.status}`);
      }

      setMvpState(S.SUCCESS);
      setMvpMsg(`✓ Report MVP inviato a ${MVP_EMAIL}`);
      setTimeout(() => { setMvpState(S.IDLE); setMvpMsg(''); }, 5000);
    } catch (err) {
      setMvpState(S.ERROR);
      const msg = err.message?.includes('Failed to fetch') || err.message?.includes('ERR_CONNECTION')
        ? 'Backend MVP non raggiungibile. Assicurati che il server sia avviato.'
        : err.message || 'Errore invio report MVP.';
      setMvpMsg(msg);
    }
  }

  return (
    <section className="launcher-section">

      {/* ── Match header ─────────────────────────────── */}
      <div className="launcher-match-header">
        <div className="launcher-match-meta">
          {matchday != null && <span className="launcher-badge">Giornata {matchday}</span>}
          {st && <span className={`match-status ${st.cls}`}>{st.text}</span>}
          {fmtDate(rawDate) && <span className="launcher-date">{fmtDate(rawDate)}</span>}
        </div>

        <div className="launcher-scoreboard">
          <div className="launcher-team launcher-team-home">
            <span className="launcher-role-label">Casa</span>
            <span className="launcher-team-acronym">{homeFull}</span>
            {hasScore && <span className="launcher-team-score">{homeScore}</span>}
          </div>
          <div className="launcher-score-center">
            {hasScore
              ? <span className="launcher-score-sep-only">–</span>
              : <span className="launcher-vs">VS</span>}
          </div>
          <div className="launcher-team launcher-team-away">
            <span className="launcher-role-label">Ospite</span>
            <span className="launcher-team-acronym">{awayFull}</span>
            {hasScore && <span className="launcher-team-score">{awayScore}</span>}
          </div>
        </div>

        <div className="launcher-extra">
          {stadium && <span className="launcher-info">🏟 {stadium}</span>}
          {matchId  && <span className="launcher-info muted">ID: <code>{matchId}</code></span>}
        </div>

        {process.env.NODE_ENV === 'development' && (
          <button onClick={() => setShowRaw(v => !v)}
            style={{marginTop:10,background:'none',border:'1px solid #333',color:'#7a90a8',
                    borderRadius:4,padding:'3px 10px',cursor:'pointer',fontSize:11}}>
            {showRaw ? 'Nascondi' : '🔍 Raw JSON'}
          </button>
        )}
        {showRaw && (
          <pre style={{marginTop:8,background:'#0a1520',padding:12,borderRadius:6,
                       fontSize:10,overflowX:'auto',color:'#aaa',maxHeight:300,overflowY:'auto'}}>
            {JSON.stringify(match, null, 2)}
          </pre>
        )}
      </div>

      {/* ── Plugins ──────────────────────────────────── */}
      {isPlayable && (
        <div className="plugins-grid">
          <div className="mvp-plugin">
            <div className="mvp-plugin-header">
              <span className="mvp-plugin-icon">⚽</span>
              <div className="mvp-plugin-title">MVP Analyst</div>
              <div className="mvp-plugin-desc">Calcolo KPI e analisi performance giocatori</div>
            </div>

            <button
              className={`mvp-plugin-btn ${mvpState === S.LOADING ? 'loading' : ''}`}
              onClick={handleSendMVP}
              disabled={mvpState === S.LOADING}
            >
              {mvpState === S.LOADING
                ? <><span className="mvp-spinner" /> Invio in corso…</>
                : <>📧 Invia Report MVP</>}
            </button>

            {mvpMsg && (
              <div className={`mvp-msg mvp-msg-${mvpState}`}>
                {mvpState === S.LOADING && <span className="mvp-spinner mvp-spinner-sm" />}
                {mvpMsg}
              </div>
            )}
          </div>

          {/* Altri plugin verranno aggiunti qui */}
          <EventPlugin matchId={matchId} seasonId={seasonId} baseName={baseName} match={match} />
        </div>
      )}

    </section>
  );
}
